import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Checkbox from '../Checkbox/Checkbox'
import MoreVert from '../icons/MoreVert'
import Menu from '../Menu/Menu'
import Progress from '../Progress/Progress'
import { ActionTypes } from './Actions'
import Cell from './Cell'
import type { TableColumn } from './TableContext'
import { useTable } from './TableContext'

function Body(): JSX.Element {
  const intl = useIntl()
  const [state, dispatch] = useTable()

  if (state.selectable) {
    const column = state.columns.selected.find(
      (column) => column.key !== 'checkbox',
    ) as TableColumn

    state.rows = state.rows.map((row) => {
      const handleChange = () => {
        dispatch({
          type: ActionTypes.ToggleRow,
          key: row.id,
          selectionsRef: state.selectionsRef,
        })
      }

      return {
        ...row,
        checkbox: (
          <Checkbox.Input
            aria-label={intl.formatMessage(
              { id: 'Table.select_row' },
              { row: `${row[column.key]}` },
            )}
            defaultChecked={row.selected}
            onChange={handleChange}
            tabIndex={-1}
          />
        ),
      }
    })
  }

  const hasRowActions = state.rowActions?.length
  const rowActionLabelKey = [
    ...state.columns.selected,
    ...state.columns.unselected,
  ].find((column) => column?.primaryLabel === true)?.key

  return (
    <tbody>
      {state.loading ? (
        <tr className="absolute inset-0 flex items-center justify-center mt-32">
          <td>
            <Progress variant="circular">
              <Progress.Description>
                <FormattedMessage id="Ui.loading" />
              </Progress.Description>
            </Progress>
          </td>
        </tr>
      ) : (
        state.rows.slice(0, Number(state.limit)).map((row) => {
          const showRowAction =
            hasRowActions &&
            state.rowActions?.some(
              (action) => action.enabled === undefined || action.enabled(row),
            )
          const rowLabelId = `${row.id}-label`
          const moreButtonLabelId = `${row.id}-button-label`

          return (
            <tr
              aria-selected={
                state.selectable
                  ? row?.selected
                    ? 'true'
                    : 'false'
                  : undefined
              }
              className="border-b border-black-10 last:border-b-0 group"
              key={row.id}
            >
              {state.columns.selected.map((column) => (
                <Cell key={column.key} selected={row?.selected}>
                  {row[column.key] ? (
                    row[column.key]
                  ) : (
                    <span className="text-black-60">—</span>
                  )}
                </Cell>
              ))}
              {state.staticColumns && !hasRowActions ? null : (
                <Cell selected={row?.selected} sticky>
                  {showRowAction ? (
                    <Menu placement="bottom-end">
                      {rowActionLabelKey ? (
                        <span className="hidden" id={rowLabelId}>
                          {`, ${row[rowActionLabelKey]}`}
                        </span>
                      ) : null}
                      <Menu.Button
                        aria-labelledby={
                          rowActionLabelKey
                            ? `${moreButtonLabelId} ${rowLabelId}`
                            : undefined
                        }
                        icon
                        label={intl.formatMessage({ id: 'Table.more_actions' })}
                      >
                        <span className="hidden" id={moreButtonLabelId}>
                          {intl.formatMessage({ id: 'Table.more_actions' })}
                        </span>
                        <MoreVert />
                      </Menu.Button>
                      <Menu.Items>
                        {state.rowActions
                          ?.filter((action) => {
                            if (action.enabled) {
                              return action.enabled(row)
                            }

                            return true
                          })
                          ?.map((action, actionIndex) => {
                            const onClick = () => {
                              action.handler(row)
                            }

                            const actionLabelId = `${actionIndex}-action-label`

                            return (
                              <div
                                className="text-black-60"
                                key={`${row.id}-${action.label}`}
                              >
                                <Menu.Item
                                  aria-labelledby={
                                    rowActionLabelKey
                                      ? `${actionLabelId} ${rowLabelId}`
                                      : undefined
                                  }
                                  onClick={onClick}
                                  startAdornment={action.startAdornment}
                                >
                                  <div
                                    className="text-black-90"
                                    id={actionLabelId}
                                  >
                                    {action.label}
                                  </div>
                                </Menu.Item>
                              </div>
                            )
                          })}
                      </Menu.Items>
                    </Menu>
                  ) : null}
                </Cell>
              )}
            </tr>
          )
        })
      )}
    </tbody>
  )
}

export default Body
